import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { Box, Button, Grid, Stack } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { ModalLayout, CardAttribute, AuthorizationGuard } from '@/components'
import { ControlledDateField, ControlledTextField } from '@/components/inputs'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs, { Dayjs } from 'dayjs'
import { Edit, XSquare } from 'react-feather'
import {
  useAuth,
  useMutationWrapper,
  usePostManualHoursReport,
  usePatchManualHoursReport,
  useModal,
} from '@/hooks'
import {
  ManualHoursReport as ManualHoursReportType,
  ManualHoursHrReport,
  UserGroups,
  PostManualHoursReport,
} from '@/types'
import {
  DATE_FORMAT_YMD,
  DATE_FORMAT_DMY_TIME,
  DATE_FORMAT_DMY,
} from '@/constants'

export type ManualHoursReportForm = {
  date: Dayjs
  amount_minutes: number | string
  description: string
}

export type ManualHoursReportProps = {
  reportData?: ManualHoursReportType | ManualHoursHrReport
}

export const ManualHoursReport = ({ reportData }: ManualHoursReportProps) => {
  const { t } = useTranslation('manualHoursReporting')
  const { close } = useModal()

  const { hasPermission } = useAuth()
  const isEmployee = hasPermission([
    UserGroups.EMPLOYEE,
    UserGroups.MANAGER,
    UserGroups.SOUND_TECHNICIAN,
    UserGroups.DATA_CONTROLLER,
  ])

  const canEditExisting = isEmployee
  const isCreate = !reportData

  const [data, setData] = useState<
    (ManualHoursReportType | ManualHoursHrReport) | undefined
  >(reportData)

  const [isEdit, setIsEdit] = useState<boolean>(!reportData)

  const schema = yup.object({
    date: yup.mixed<Dayjs>().required(),
    amount_minutes: yup
      .number()
      .transform((v, o) => (o === '' || o === null ? null : v))
      .integer(t('errors.invalidInteger'))
      .min(0, t('errors.mustBeNonNegative'))
      .required()
      .nullable(true),
    description: yup.string().required(),
  })

  const defaultValues: ManualHoursReportForm = {
    date: reportData?.date ? dayjs(reportData.date) : dayjs(),
    amount_minutes: reportData?.amount_minutes ?? '',
    description: reportData?.description ?? '',
  }

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm<ManualHoursReportForm>({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  })

  const { mutateAsync: createItem, isLoading: creating } = useMutationWrapper(
    () => usePostManualHoursReport()
  )
  const { mutateAsync: updateItem, isLoading: updating } = useMutationWrapper(
    () => usePatchManualHoursReport(reportData?.id as number)
  )

  const isSaving = creating || updating

  const onSubmit = async (form: ManualHoursReportForm) => {
    const payload = {
      amount_minutes: Number(form.amount_minutes),
      date: form.date.format(DATE_FORMAT_YMD),
      description: form.description || null,
    } as PostManualHoursReport

    if (isCreate) {
      await createItem(payload, {
        successMessageKey: 'manualHoursReporting:notifications.saved',
        onSuccess: () => {
          close()
        },
      })
    } else if (canEditExisting && data) {
      await updateItem(payload, {
        successMessageKey: 'manualHoursReporting:notifications.updated',
        onSuccess: (res) => {
          setData(res)
          setIsEdit(false)
        },
      })
    }
  }

  const title = isCreate ? t('createTitle') : t('detailTitle', { id: data?.id })

  return (
    <ModalLayout title={title}>
      <Stack spacing={4}>
        {!!data && (
          <Grid container={true} spacing={2}>
            {(data as ManualHoursHrReport).user && (
              <Grid item={true} xs={12} md={6}>
                <CardAttribute
                  title={t('fieldTitles.user')}
                  value={
                    `${(data as ManualHoursHrReport).user.first_name ?? ''} ${
                      (data as ManualHoursHrReport).user.last_name ?? ''
                    }`.trim() || (data as ManualHoursHrReport).user.email
                  }
                />
              </Grid>
            )}
            <Grid item={true} xs={12} md={6}>
              <CardAttribute
                title={t('fieldTitles.created')}
                value={
                  data.created_at
                    ? dayjs(data.created_at).format(DATE_FORMAT_DMY_TIME)
                    : '-'
                }
              />
            </Grid>
            <Grid item={true} xs={12} md={6}>
              <CardAttribute
                title={t('fieldTitles.updated')}
                value={
                  data.updated_at
                    ? dayjs(data.updated_at).format(DATE_FORMAT_DMY_TIME)
                    : '-'
                }
              />
            </Grid>
            {!isEmployee && (
              <Grid item={true} xs={12} md={6}>
                <CardAttribute
                  title={t('fieldTitles.exported')}
                  value={
                    data.exported_at
                      ? dayjs(data.exported_at).format(DATE_FORMAT_DMY_TIME)
                      : '-'
                  }
                />
              </Grid>
            )}
          </Grid>
        )}

        {!isCreate && (
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={4}
            width="100%"
          >
            <span />
            <AuthorizationGuard
              allowedGroups={[UserGroups.EMPLOYEE]}
              redirect={false}
            >
              {canEditExisting && (
                <Button
                  onClick={() => setIsEdit((v) => !v)}
                  variant="textThin"
                  endIcon={isEdit ? <XSquare size={14} /> : <Edit size={14} />}
                >
                  {isEdit ? t('buttons.closeEditing') : t('buttons.edit')}
                </Button>
              )}
            </AuthorizationGuard>
          </Stack>
        )}

        {isEdit || isCreate ? (
          <>
            <Stack spacing={4}>
              <Stack direction="row" spacing={4}>
                <Box>
                  <ControlledDateField<ManualHoursReportForm>
                    name="date"
                    control={control}
                    placeholder={t('placeholders.date')}
                  />
                </Box>
                <Box>
                  <ControlledTextField<ManualHoursReportForm>
                    name="amount_minutes"
                    control={control}
                    type="number"
                    placeholder={t('placeholders.amountMinutes')}
                    inputProps={{ min: 2 }}
                  />
                </Box>
              </Stack>
              <ControlledTextField<ManualHoursReportForm>
                name="description"
                control={control}
                multiline={true}
                minRows={3}
                placeholder={t('placeholders.description')}
              />
            </Stack>

            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              {(isCreate || canEditExisting) && (
                <LoadingButton
                  variant="contained"
                  onClick={handleSubmit(onSubmit)}
                  loading={isSaving}
                  disabled={!isValid || (!isCreate && !isDirty)}
                >
                  {isCreate ? t('buttons.create') : t('buttons.save')}
                </LoadingButton>
              )}
            </Stack>
          </>
        ) : (
          <Grid container={true} spacing={2}>
            <Grid item={true} xs={12} md={6}>
              <CardAttribute
                title={t('fieldTitles.date')}
                value={
                  (data?.date && dayjs(data.date).format(DATE_FORMAT_DMY)) ||
                  '-'
                }
              />
            </Grid>
            <Grid item={true} xs={12} md={6}>
              <CardAttribute
                title={t('fieldTitles.amountMinutes')}
                value={data?.amount_minutes ?? '-'}
              />
            </Grid>
            <Grid item={true} xs={12}>
              <CardAttribute
                title={t('fieldTitles.description')}
                value={data?.description || '-'}
              />
            </Grid>
          </Grid>
        )}
      </Stack>
    </ModalLayout>
  )
}
